/**
 * NPC Mind Store — Ephemeral GM-only NPC reasoning state
 *
 * No Supabase, no realtime. Just shows the GM what NPC AI is "thinking".
 * Stores latest decision + derived thoughts per NPC charId.
 */

import { create } from 'zustand';
import type { NpcDecision, NpcCombatContext } from '@machina/core';
import { deriveNpcThoughts } from '@machina/core';

export interface NpcMindEntry {
  charId: string;
  charName: string;
  thoughts: string[];
  lastDecision: NpcDecision;
  lastContext: NpcCombatContext;
  timestamp: number;
}

interface NpcMindState {
  minds: Record<string, NpcMindEntry>;
  pushState(charId: string, charName: string, decision: NpcDecision, ctx: NpcCombatContext): void;
  clear(charId: string): void;
  clearAll(): void;
}

export const useNpcMindStore = create<NpcMindState>((set) => ({
  minds: {},

  pushState(charId, charName, decision, ctx) {
    const thoughts = deriveNpcThoughts(ctx, decision);
    set((state) => ({
      minds: {
        ...state.minds,
        [charId]: {
          charId,
          charName,
          thoughts,
          lastDecision: decision,
          lastContext: ctx,
          timestamp: Date.now(),
        },
      },
    }));
  },

  clear(charId) {
    set((state) => {
      const rest = { ...state.minds };
      delete rest[charId];
      return { minds: rest };
    });
  },

  clearAll() {
    set({ minds: {} });
  },
}));
